import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, Button, Container, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo);
    }
  }

  logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    console.error('Logging error to service:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md" className="mt-8">
          <Alert severity="error" className="mb-4">
            <AlertTitle>Something went wrong</AlertTitle>
            <Typography variant="body1" className="mb-4">
              An unexpected error occurred. Please try refreshing the page.
            </Typography>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-4 p-4 bg-gray-100 rounded border">
                <Typography variant="h6" className="mb-2">
                  Error Details (Development Only):
                </Typography>
                <Typography variant="body2" component="pre" className="whitespace-pre-wrap">
                  {this.state.error.message}
                  {this.state.error.stack}
                </Typography>
                {this.state.errorInfo && (
                  <Typography variant="body2" component="pre" className="whitespace-pre-wrap mt-2">
                    {this.state.errorInfo.componentStack}
                  </Typography>
                )}
              </div>
            )}
            
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleReload}
              className="mt-4"
            >
              Reload Page
            </Button>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;